import "@/App.css";
import { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "./context/CartContext";
import { ThemeProvider } from "./context/ThemeContext";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";

// Footer is below-the-fold on every page → lazy load
const Footer = lazy(() => import("./components/Footer"));
// Lazy load below-fold layout components
const MobileBottomBar = lazy(() => import("./components/MobileBottomBar"));

const LazyToaster = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

function DeferredToaster() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setReady(true));
      return () => cancelIdleCallback(id);
    } else {
      const t = setTimeout(() => setReady(true), 2000);
      return () => clearTimeout(t);
    }
  }, []);
  if (!ready) return null;
  return <Suspense fallback={null}><LazyToaster position="top-right" richColors /></Suspense>;
}

// Lazy load ALL pages including HomePage
const HomePage = lazy(() => import("./pages/HomePage"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const ReturnPolicyPage = lazy(() => import("./pages/ReturnPolicyPage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminPromo = lazy(() => import("./pages/admin/AdminPromo"));
const AdminFAQ = lazy(() => import("./pages/admin/AdminFAQ"));
const AdminAccounts = lazy(() => import("./pages/admin/AdminAccounts"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Lazy load non-critical layout components
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const CallNowButton = lazy(() => import("./components/CallNowButton"));
const CartSheet = lazy(() => import("./components/CartSheet"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));

function PageLoader() {
  return <div className="flex items-center justify-center min-h-[40vh]"><div className="w-8 h-8 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" /></div>;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="main-content" style={{ minHeight: '100vh' }}>
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </main>
      <Suspense fallback={null}><Footer /></Suspense>
      <Suspense fallback={null}><MobileBottomBar /></Suspense>
      <Suspense fallback={null}><WhatsAppButton /></Suspense>
      <Suspense fallback={null}><CallNowButton /></Suspense>
      <Suspense fallback={null}><CartSheet /></Suspense>
      <Suspense fallback={null}><CookieConsent /></Suspense>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <DeferredToaster />
          <Routes>
            {/* Public */}
            <Route path="/" element={<PublicLayout><Suspense fallback={null}><HomePage /></Suspense></PublicLayout>} />
            <Route path="/magazin" element={<PublicLayout><ShopPage /></PublicLayout>} />
            <Route path="/produs/:id" element={<PublicLayout><ProductPage /></PublicLayout>} />
            <Route path="/despre-noi" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/stiai-ca" element={<PublicLayout><BlogPage /></PublicLayout>} />
            <Route path="/stiai-ca/:slug" element={<PublicLayout><BlogArticlePage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/cos" element={<PublicLayout><CartPage /></PublicLayout>} />
            <Route path="/termeni-si-conditii" element={<PublicLayout><TermsPage /></PublicLayout>} />
            <Route path="/politica-confidentialitate" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
            <Route path="/politica-cookies" element={<PublicLayout><CookiesPage /></PublicLayout>} />
            <Route path="/politica-returnare" element={<PublicLayout><ReturnPolicyPage /></PublicLayout>} />

            {/* 404 */}
            <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />

            {/* Admin */}
            <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminLayout /></Suspense>}>
              <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
              <Route path="produse" element={<Suspense fallback={<PageLoader />}><AdminProducts /></Suspense>} />
              <Route path="comenzi" element={<Suspense fallback={<PageLoader />}><AdminOrders /></Suspense>} />
              <Route path="blog" element={<Suspense fallback={<PageLoader />}><AdminBlog /></Suspense>} />
              <Route path="promo" element={<Suspense fallback={<PageLoader />}><AdminPromo /></Suspense>} />
              <Route path="faq" element={<Suspense fallback={<PageLoader />}><AdminFAQ /></Suspense>} />
              <Route path="conturi" element={<Suspense fallback={<PageLoader />}><AdminAccounts /></Suspense>} />
              <Route path="mesaje" element={<Suspense fallback={<PageLoader />}><AdminMessages /></Suspense>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </CartProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
